import { isPrerendering } from '../rumPage'
import { whenAyReady } from './assertive-yield/queue'

/**
 * Readiness points of the ad stack, each sent to Datadog RUM once per page load
 * as a duration vital from the start of the page, next to `first_ad_visible`:
 *
 * - `gpt_ready`: GPT loaded and ran its command queue.
 * - `ay_ready`: the AY analytics SDK loaded and ran its queue.
 *
 * The qz.com reader pages send the same names with the same meaning, so the
 * two apps can be read side by side. A script's RUM resource only says when its
 * download finished, not when the vendor was ready, so each point is taken from
 * the vendor's own queue. Both queues are seeded before the AY manager loads
 * (and with it GPT), so each callback runs when its vendor drains the queue.
 *
 * Each point also leaves an `ad-readiness:<name>` performance mark, so it shows
 * up in a DevTools trace. A point reached while the page is still being
 * prerendered was already there when the visitor saw the page, so it is
 * reported as 0 once the page is shown.
 */

export type ReadinessMilestone = 'gpt_ready' | 'ay_ready'

export const READINESS_MARK_PREFIX = 'ad-readiness:'

/** Past this, a milestone says nothing about how the page loaded. Same cap as `first_ad_visible`. */
export const READINESS_MAX_MS = 60_000

export interface ReadinessSample {
  name: ReadinessMilestone
  /** Ms from the start of the page until the milestone. */
  duration: number
}

export interface ReadinessMilestones {
  /** The landing page is over: milestones reached after this are dropped. */
  stop: () => void
}

// Once GPT has run its queue the moment is lost, and timing it now would
// report when this happened to look, not when GPT was ready.
const gptAlreadyReady = () => window.googletag?.apiReady === true

// AY swaps `push` on its queue once it loads (see assertive-yield/queue.ts).
const ayAlreadyReady = () =>
  window.assertiveQueue !== undefined &&
  window.assertiveQueue.push !== Array.prototype.push

/** Ms from the time origin, the mark's own time when marks are supported. */
const markNow = (name: ReadinessMilestone): number => {
  try {
    return performance.mark(`${READINESS_MARK_PREFIX}${name}`).startTime
  } catch {
    return performance.now()
  }
}

export function watchReadinessMilestones({
  pageStart,
  report,
}: {
  /** Ms from the time origin the page was shown, read when a milestone is reached after that. */
  pageStart: () => number
  report: (sample: ReadinessSample) => void
}): ReadinessMilestones {
  let stopped = false

  const reach = (name: ReadinessMilestone) => {
    // Runs inside a vendor's queue: a measurement must never throw there.
    try {
      if (stopped) return
      const markedAt = markNow(name)
      if (isPrerendering()) {
        // The page start is not known until the page is shown.
        document.addEventListener(
          'prerenderingchange',
          () => {
            if (!stopped) report({ name, duration: 0 })
          },
          { once: true }
        )
        return
      }
      const duration = Math.max(0, markedAt - pageStart())
      if (duration > READINESS_MAX_MS) return
      report({ name, duration })
    } catch {
      // Lose the milestone, keep the vendor's queue running.
    }
  }

  if (!gptAlreadyReady()) {
    window.googletag = window.googletag || { cmd: [] }
    window.googletag.cmd.push(() => reach('gpt_ready'))
  }
  if (!ayAlreadyReady()) {
    whenAyReady(() => reach('ay_ready'))
  }

  return {
    stop: () => {
      stopped = true
    },
  }
}
